
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/contexts/notifications';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CreditCard, GraduationCap, Calendar, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTeacherNotifications } from '@/hooks/useTeacherNotifications';
import { StudentNotificationsTab, TeacherNotificationsTab } from './NotificationSummaryTabs';
import { Notification } from '@/contexts/notifications/types';

export const NotificationSummary: React.FC = () => {
  const { notifications, markAsRead } = useNotifications();
  const { 
    notifications: teacherNotifications, 
    markAsRead: markTeacherNotificationAsRead 
  } = useTeacherNotifications();
  
  const navigate = useNavigate();
  
  // Ensure notifications match the required type with priority property
  const typedNotifications = notifications as Notification[];
  
  const completionNotifications = typedNotifications.filter(n => n.type === 'completion');
  const paymentNotifications = typedNotifications.filter(n => n.type === 'payment');
  
  const contractNotifications = teacherNotifications.filter(n => n.contentType === 'contract-expiration');
  const salaryNotifications = teacherNotifications.filter(n => n.contentType === 'salary-reminder');
  
  const handleStudentNotificationClick = (notification: any) => {
    markAsRead(notification.id);
    if (notification.studentId) {
      navigate(`/students/${notification.studentId}`);
    }
  };
  
  const handleTeacherNotificationClick = (notification: any) => {
    markTeacherNotificationAsRead(notification.id);
    navigate(`/teachers/${notification.teacherId}`);
  };
  
  return (
    <Card className="border-0 shadow-none">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Notifications Summary</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Tabs defaultValue="completions">
          <TabsList className="w-full grid grid-cols-4 rounded-none border-b bg-transparent">
            <TabsTrigger value="completions" className="flex items-center gap-1 text-xs data-[state=active]:bg-transparent">
              <GraduationCap className="h-3 w-3" />
              <span>Completions</span>
              {completionNotifications.length > 0 && (
                <span className="ml-1 text-xs bg-slate-200 dark:bg-slate-700 rounded-full px-1.5 py-0.5">
                  {completionNotifications.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="payments" className="flex items-center gap-1 text-xs data-[state=active]:bg-transparent">
              <CreditCard className="h-3 w-3" />
              <span>Payments</span>
              {paymentNotifications.length > 0 && (
                <span className="ml-1 text-xs bg-slate-200 dark:bg-slate-700 rounded-full px-1.5 py-0.5">
                  {paymentNotifications.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="contracts" className="flex items-center gap-1 text-xs data-[state=active]:bg-transparent">
              <Calendar className="h-3 w-3" />
              <span>Contracts</span>
              {contractNotifications.length > 0 && (
                <span className="ml-1 text-xs bg-slate-200 dark:bg-slate-700 rounded-full px-1.5 py-0.5">
                  {contractNotifications.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="salaries" className="flex items-center gap-1 text-xs data-[state=active]:bg-transparent">
              <Clock className="h-3 w-3" />
              <span>Salaries</span>
              {salaryNotifications.length > 0 && (
                <span className="ml-1 text-xs bg-slate-200 dark:bg-slate-700 rounded-full px-1.5 py-0.5">
                  {salaryNotifications.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="completions" className="m-0">
            <StudentNotificationsTab 
              notifications={typedNotifications}
              type="completion"
              onNotificationClick={handleStudentNotificationClick}
            />
          </TabsContent>
          
          <TabsContent value="payments" className="m-0">
            <StudentNotificationsTab 
              notifications={typedNotifications}
              type="payment"
              onNotificationClick={handleStudentNotificationClick}
            />
          </TabsContent>
          
          <TabsContent value="contracts" className="m-0">
            <TeacherNotificationsTab 
              notifications={teacherNotifications}
              contentType="contract-expiration"
              onNotificationClick={handleTeacherNotificationClick}
            />
          </TabsContent>
          
          <TabsContent value="salaries" className="m-0">
            <TeacherNotificationsTab 
              notifications={teacherNotifications}
              contentType="salary-reminder"
              onNotificationClick={handleTeacherNotificationClick}
            />
          </TabsContent>
        </Tabs>
        
        <div className="p-3 border-t">
          <div className="grid grid-cols-2 gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full"
              onClick={() => navigate('/students')}
            >
              View Students
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full"
              onClick={() => navigate('/teachers')}
            >
              View Teachers
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
